import { useEffect, useState } from 'react'
import { RefreshCw, TrendingUp, Heart, MessageCircle, Share2, ExternalLink, Music, Play } from 'lucide-react'

interface TikTokTrend {
  id: string
  hashtag: string
  views: number
  videos: number
  growth: string
  relatedTo: string[]
  sound?: string
}

interface TikTokVideo {
  id: string
  thumbnail: string
  description: string
  likes: number
  comments: number
  shares: number
  views: number
  date: string
  hashtags: string[]
  sound?: string
}

export default function TikTokPage() {
  const [trends, setTrends] = useState<TikTokTrend[]>([])
  const [videos, setVideos] = useState<TikTokVideo[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchTikTokData()
  }, [])

  const fetchTikTokData = async () => {
    setLoading(true)
    // TODO: Implementar chamada real à API do TikTok
    // Por enquanto, dados mockados baseados em trends relacionadas à marca
    setTimeout(() => {
      setTrends([
        {
          id: '1',
          hashtag: '#guiltybeauty',
          views: 2500000,
          videos: 8500,
          growth: '+25%',
          relatedTo: ['beauty', 'makeup', 'skincare'],
          sound: 'Original Sound'
        },
        {
          id: '2',
          hashtag: '#bperfectcosmetics',
          views: 8500000,
          videos: 28000,
          growth: '+28%',
          relatedTo: ['makeup', 'palettes', 'bperfect'],
          sound: 'Trending Sound'
        },
        {
          id: '3',
          hashtag: '#danessamyricks',
          views: 4200000,
          videos: 15000,
          growth: '+32%',
          relatedTo: ['makeup', 'foundation', 'danessa'],
          sound: 'Viral Sound'
        },
        {
          id: '4',
          hashtag: '#kryolan',
          views: 6800000,
          videos: 22000,
          growth: '+20%',
          relatedTo: ['makeup', 'professional', 'kryolan'],
          sound: 'Original Sound'
        },
        {
          id: '5',
          hashtag: '#cerave',
          views: 25000000,
          videos: 75000,
          growth: '+35%',
          relatedTo: ['skincare', 'dermatologist', 'cerave'],
          sound: 'Trending Sound'
        },
        {
          id: '6',
          hashtag: '#bioderma',
          views: 18000000,
          videos: 55000,
          growth: '+22%',
          relatedTo: ['skincare', 'dermatological', 'bioderma'],
          sound: 'Viral Sound'
        },
        {
          id: '7',
          hashtag: '#isdin',
          views: 9500000,
          videos: 32000,
          growth: '+28%',
          relatedTo: ['skincare', 'sunscreen', 'isdin'],
          sound: 'Original Sound'
        },
        {
          id: '8',
          hashtag: '#foundation',
          views: 45000000,
          videos: 120000,
          growth: '+15%',
          relatedTo: ['makeup', 'base', 'foundation'],
          sound: 'Trending Sound'
        },
        {
          id: '9',
          hashtag: '#eyeshadow',
          views: 32000000,
          videos: 95000,
          growth: '+18%',
          relatedTo: ['makeup', 'eyes', 'palette'],
          sound: 'Viral Sound'
        },
        {
          id: '10',
          hashtag: '#lipstick',
          views: 28000000,
          videos: 85000,
          growth: '+16%',
          relatedTo: ['makeup', 'lips', 'lipstick'],
          sound: 'Original Sound'
        },
        {
          id: '11',
          hashtag: '#makeupbrushes',
          views: 12000000,
          videos: 38000,
          growth: '+24%',
          relatedTo: ['brushes', 'tools', 'makeup'],
          sound: 'Trending Sound'
        },
        {
          id: '12',
          hashtag: '#waterproofmakeup',
          views: 8500000,
          videos: 28000,
          growth: '+30%',
          relatedTo: ['makeup', 'waterproof', 'longlasting'],
          sound: 'Viral Sound'
        },
        {
          id: '13',
          hashtag: '#makeuppalette',
          views: 15000000,
          videos: 48000,
          growth: '+20%',
          relatedTo: ['palette', 'makeup', 'colors'],
          sound: 'Original Sound'
        },
        {
          id: '14',
          hashtag: '#skincareroutine',
          views: 8500000,
          videos: 22000,
          growth: '+30%',
          relatedTo: ['skincare', 'routine', 'selfcare'],
          sound: 'Trending Sound'
        },
        {
          id: '15',
          hashtag: '#makeuptutorial',
          views: 12000000,
          videos: 35000,
          growth: '+12%',
          relatedTo: ['makeup', 'tutorial', 'beauty'],
          sound: 'Viral Sound'
        }
      ])
      setVideos([
        {
          id: '1',
          thumbnail: 'https://via.placeholder.com/300x400?text=TikTok+Video',
          description: 'Novo produto chegando! ✨ #guiltybeauty #beauty',
          likes: 12500,
          comments: 450,
          shares: 320,
          views: 125000,
          date: '2024-01-13',
          hashtags: ['#guiltybeauty', '#beauty', '#newproduct'],
          sound: 'Original Sound - Guilty Beauty'
        }
      ])
      setLoading(false)
    }, 1000)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TikTok</h1>
          <p className="text-gray-600 mt-1">Trends e análise da marca</p>
          <a 
            href="https://www.tiktok.com/@guiltybeauty.oficial" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-black mt-2 inline-flex items-center gap-1"
          >
            @guiltybeauty.oficial <ExternalLink className="w-3 h-3" />
          </a>
        </div>
        <button
          onClick={fetchTikTokData}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-900 transition"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Seguidores</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <TrendingUp className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Visualizações</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <Play className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Curtidas</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <Heart className="w-8 h-8 text-black" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Vídeos</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">0</p>
            </div>
            <MessageCircle className="w-8 h-8 text-black" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Music className="w-5 h-5" />
            Trends Relacionadas à Marca
          </h2>
        </div>
        <div className="p-6">
          <div className="space-y-4">
            {trends.map((trend) => (
              <div key={trend.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-lg text-black">{trend.hashtag}</span>
                      <span className={`text-sm font-medium ${
                        trend.growth.startsWith('+') ? 'text-black' : 'text-gray-600'
                      }`}>
                        {trend.growth}
                      </span>
                    </div>
                    {trend.sound && (
                      <div className="flex items-center gap-1 mb-2 text-sm text-gray-600">
                        <Music className="w-3 h-3" />
                        <span>{trend.sound}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Visualizações</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {(trend.views / 1000000).toFixed(1)}M
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Vídeos</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {trend.videos.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Crescimento</p>
                        <p className="text-sm font-semibold text-gray-900">{trend.growth}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Relacionado</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {trend.relatedTo.slice(0, 2).map((tag, idx) => (
                            <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Vídeos Recentes</h2>
        </div>
        <div className="p-6">
          {videos.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nenhum vídeo encontrado</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {videos.map((video) => (
                <div key={video.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="relative">
                    <img 
                      src={video.thumbnail} 
                      alt={video.description}
                      className="w-full h-64 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-20 transition flex items-center justify-center">
                      <Play className="w-12 h-12 text-white opacity-0 hover:opacity-100 transition" />
                    </div>
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                      {(video.views / 1000).toFixed(0)}K views
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-700 line-clamp-2 mb-3">{video.description}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          {(video.likes / 1000).toFixed(1)}K
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-4 h-4" />
                          {video.comments}
                        </span>
                        <span className="flex items-center gap-1">
                          <Share2 className="w-4 h-4" />
                          {video.shares}
                        </span>
                      </div>
                    </div>
                    {video.sound && (
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                        <Music className="w-3 h-3" />
                        <span className="truncate">{video.sound}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1">
                      {video.hashtags.map((tag, idx) => (
                        <span key={idx} className="text-xs text-gray-600">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
